import type { WinterMonthlyWeather } from '@/cms/fields/types/content-page'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useIntl } from '@/intl'
import { computeSnowTiers, hasSnowHeight, orderWinterMonths } from '@/scopes/content/domain/weather'
import { WeatherGraph, WeatherGraphHeading } from './WeatherGraph'
import styles from './WinterWeatherGraph.module.css'
import { WinterWeatherGraphColumn } from './WinterWeatherGraphColumn'

interface WinterWeatherGraphProps {
  months: WinterMonthlyWeather[]
  minAltitudeMeters: number | null
  maxAltitudeMeters: number | null
}

export function WinterWeatherGraph({ months, minAltitudeMeters, maxAltitudeMeters }: WinterWeatherGraphProps) {
  const intl = useIntl()
  const monthsWithSnow = orderWinterMonths(months.filter(hasSnowHeight))

  if (monthsWithSnow.length === 0)
    return null

  const maxSnowDepthCm = Math.max(...monthsWithSnow.map(month => month.snowDepthTopCm))
  const snowTiers = computeSnowTiers(monthsWithSnow)

  return (
    <WeatherGraph ariaLabel={intl.formatMessage({ id: 'content_weather_report_graph_aria_label' })}>
      <WeatherGraphHeading className={styles.chartLabels}>
        <span className={styles.monthLabelSpacer} />
        <div className={styles.snowHeightLabels}>
          <Typography variant="body-s-regular-tight">
            {intl.formatMessage(
              { id: 'content_weather_report_graph_heading_snow_height_top' },
              { elevation: maxAltitudeMeters ?? '—' },
            )}
          </Typography>
          <Typography variant="body-s-regular-tight">
            {intl.formatMessage(
              { id: 'content_weather_report_graph_heading_snow_height_bottom' },
              { elevation: minAltitudeMeters ?? '—' },
            )}
          </Typography>
        </div>
        <Typography className={styles.rowLabel} variant="body-s-regular-tight">
          {intl.formatMessage({ id: 'content_weather_report_graph_heading_snow_days' })}
        </Typography>
      </WeatherGraphHeading>
      {monthsWithSnow.map(month => (
        <WinterWeatherGraphColumn
          key={month.month}
          data={month}
          maxSnowDepthCm={maxSnowDepthCm}
          tier={snowTiers.get(month.month)!}
        />
      ))}
    </WeatherGraph>
  )
}
